import { useEffect, useMemo, useRef, useState } from "react";
import "./comment-layer.css";
import "./comment-auth.css";

type Comment = {
  id: string;
  x: number;
  y: number;
  body: string;
  author: string;
  createdAt: string;
  authorEmail?: string;
  authorPicture?: string;
  immutable?: boolean;
  source?: "web" | "publisher";
  normalized?: boolean;
  anchor?: { selector: string; xRatio: number; yRatio: number };
};
type Draft = { x: number; y: number; normalized: true; anchor?: Comment["anchor"] };
type GoogleUser = { email: string; name: string; picture?: string };
const GOOGLE_WEB_CLIENT_ID = "512175910464-9q623m32fhtke0r5ovtksk167f2fvmrj.apps.googleusercontent.com";

const storageKey = () => `flora:comments:${window.location.pathname}`;

function readComments(): Comment[] {
  try { return JSON.parse(localStorage.getItem(storageKey()) || "[]"); }
  catch { return []; }
}

export default function CommentLayer() {
  const [enabled, setEnabled] = useState(false);
  const [comments, setComments] = useState<Comment[]>(readComments);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [body, setBody] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [serverError, setServerError] = useState("");
  const [, setViewportVersion] = useState(0);
  const [googleCredential, setGoogleCredential] = useState(() => sessionStorage.getItem("flora:comment-google") || "");
  const [googleUser, setGoogleUser] = useState<GoogleUser | null>(() => { try { return JSON.parse(sessionStorage.getItem("flora:comment-user") || "null"); } catch { return null; } });
  const googleButton = useRef<HTMLDivElement>(null);
  const selectedComment = useMemo(() => comments.find(comment => comment.id === selected), [comments, selected]);

  useEffect(() => {
    const toggle = (event: KeyboardEvent) => {
      if (event.shiftKey && (event.code === "KeyC" || event.key.toLowerCase() === "c")) {
        if (event.repeat) return;
        event.preventDefault();
        event.stopPropagation();
        setEnabled(value => !value);
        setDraft(null);
        setSelected(null);
      }
      if (event.key === "Escape") { setDraft(null); setSelected(null); }
    };
    window.addEventListener("keydown", toggle, true);
    return () => window.removeEventListener("keydown", toggle, true);
  }, []);
  useEffect(() => {
    const update = () => setViewportVersion(value => value + 1);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  useEffect(() => {
    if (!enabled || googleUser) return;
    const initialize = () => {
      const api = (window as any).google?.accounts?.id;
      if (!api || !googleButton.current) return;
      api.initialize({ client_id: GOOGLE_WEB_CLIENT_ID, hd: "postmath.co.kr", callback: ({ credential }: { credential: string }) => {
        try { const payload = JSON.parse(atob(credential.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))); const user = { email: payload.email, name: payload.name || payload.email, picture: payload.picture }; sessionStorage.setItem("flora:comment-google", credential); sessionStorage.setItem("flora:comment-user", JSON.stringify(user)); setGoogleCredential(credential); setGoogleUser(user); } catch { setServerError("Google 로그인 정보를 읽지 못했습니다."); }
      }});
      googleButton.current.innerHTML = ""; api.renderButton(googleButton.current, { theme: "outline", size: "medium", text: "signin_with", shape: "pill" });
    };
    let script = document.querySelector<HTMLScriptElement>('script[src="https://accounts.google.com/gsi/client"]');
    if (!script) { script = document.createElement("script"); script.src = "https://accounts.google.com/gsi/client"; script.async = true; document.head.appendChild(script); }
    script.addEventListener("load", initialize, { once: true }); if ((window as any).google) initialize();
  }, [enabled, googleUser]);

  const pageSize = () => ({
    width: Math.max(document.documentElement.scrollWidth, document.body.scrollWidth, window.innerWidth),
    height: Math.max(document.documentElement.scrollHeight, document.body.scrollHeight, window.innerHeight),
  });
  const point = (comment: Pick<Comment, "x" | "y" | "normalized" | "anchor">) => {
    if (comment.anchor) {
      try {
        const element = document.querySelector(comment.anchor.selector);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) return {
            x: rect.left + window.scrollX + rect.width * comment.anchor.xRatio,
            y: rect.top + window.scrollY + rect.height * comment.anchor.yRatio,
          };
        }
      } catch { /* use fallback coordinates */ }
    }
    const size = pageSize();
    return comment.normalized || (comment.x <= 1 && comment.y <= 1)
      ? { x: comment.x * size.width, y: comment.y * size.height }
      : { x: comment.x, y: comment.y };
  };

  const selectorFor = (element: Element) => {
    if (element.id) return `#${CSS.escape(element.id)}`;
    const classes = Array.from(element.classList).filter(name => !name.startsWith("flora-comment"));
    if (classes.length) {
      const selector = `.${classes.map(name => CSS.escape(name)).join(".")}`;
      if (document.querySelectorAll(selector).length === 1) return selector;
    }
    const parts: string[] = [];
    let current: Element | null = element;
    while (current && current !== document.body && parts.length < 7) {
      const tag = current.tagName.toLowerCase();
      const siblings = current.parentElement ? Array.from(current.parentElement.children).filter(child => child.tagName === current!.tagName) : [];
      const suffix = siblings.length > 1 ? `:nth-of-type(${siblings.indexOf(current) + 1})` : "";
      parts.unshift(`${tag}${suffix}`);
      const selector = parts.join(" > ");
      if (document.querySelectorAll(selector).length === 1) return selector;
      current = current.parentElement;
    }
    return parts.join(" > ");
  };

  const anchorAt = (clientX: number, clientY: number, overlay: HTMLDivElement) => {
    overlay.style.pointerEvents = "none";
    const raw = document.elementFromPoint(clientX, clientY);
    overlay.style.pointerEvents = "";
    if (!raw || raw === document.body || raw === document.documentElement) return undefined;
    let element: Element | null = raw;
    while (element && element !== document.body) {
      const rect = element.getBoundingClientRect();
      if (rect.width >= 24 && rect.height >= 16) {
        const selector = selectorFor(element);
        if (selector) return {
          selector,
          xRatio: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
          yRatio: Math.max(0, Math.min(1, (clientY - rect.top) / rect.height)),
        };
      }
      element = element.parentElement;
    }
    return undefined;
  };

  useEffect(() => { localStorage.setItem(storageKey(), JSON.stringify(comments)); }, [comments]);
  useEffect(() => {
    fetch(`/api/comments?page=${encodeURIComponent(window.location.pathname)}`, { cache: "no-store" })
      .then(response => { if (!response.ok) throw new Error(); return response.json() as Promise<Comment[]>; })
      .then(serverComments => { setComments(serverComments); setServerError(""); })
      .catch(() => setServerError("서버 코멘트를 불러오지 못했습니다."));
  }, []);

  const placeComment = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    event.stopPropagation();
    setSelected(null);
    setBody("");
    const size = pageSize();
    setDraft({ x: (event.clientX + window.scrollX) / size.width, y: (event.clientY + window.scrollY) / size.height, normalized: true, anchor: anchorAt(event.clientX, event.clientY, event.currentTarget) });
  };

  const save = async () => {
    if (!draft || !body.trim()) return;
    try {
      const response = await fetch(`/api/comments?page=${encodeURIComponent(window.location.pathname)}`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ x: draft.x, y: draft.y, normalized: true, anchor: draft.anchor, body: body.trim(), googleCredential: googleCredential || undefined }) });
      if (!response.ok) throw new Error();
      const comment = await response.json() as Comment;
      setComments(current => [...current, comment]);
      setDraft(null); setBody(""); setServerError("");
    } catch { setServerError("코멘트를 서버에 저장하지 못했습니다."); }
  };

  const remove = async (id: string) => {
    try {
      const response = await fetch(`/api/comments?page=${encodeURIComponent(window.location.pathname)}&id=${encodeURIComponent(id)}`, { method: "DELETE", headers: googleCredential ? { authorization: `Bearer ${googleCredential}` } : undefined });
      if (!response.ok) throw new Error();
      setComments(current => current.filter(comment => comment.id !== id));
      setSelected(null); setServerError("");
    } catch { setServerError("코멘트를 서버에서 삭제하지 못했습니다."); }
  };
  const edit = async (comment: Comment) => {
    const body = window.prompt("코멘트를 수정하세요.", comment.body)?.trim();
    if (!body || !googleCredential) return;
    try {
      const response = await fetch(`/api/comments?page=${encodeURIComponent(window.location.pathname)}`, { method: "PATCH", headers: { "content-type": "application/json", authorization: `Bearer ${googleCredential}` }, body: JSON.stringify({ id: comment.id, body }) });
      if (!response.ok) throw new Error();
      const updated = await response.json() as Comment;
      setComments(current => current.map(item => item.id === updated.id ? updated : item)); setServerError("");
    } catch { setServerError("작성한 배포자만 코멘트를 수정할 수 있습니다."); }
  };

  return <>
    <button className={`flora-comment-toggle ${enabled ? "active" : ""}`} onClick={() => setEnabled(value => !value)}>
      <span>{enabled ? "코멘트 모드 켜짐" : "코멘트"}</span><kbd>⇧ C</kbd>
    </button>
    {enabled && <div className="flora-comment-layer" onClick={placeComment} aria-label="코멘트를 남길 위치를 클릭하세요">
      <div className={`flora-comment-guide ${serverError ? "error" : ""}`}>{serverError || "화면을 클릭해 공유 코멘트를 남겨보세요"}</div>
      {comments.map((comment, index) => { const position = point(comment); return <button key={comment.id} className={`flora-comment-pin ${selected === comment.id ? "selected" : ""}`} style={{ left: position.x, top: position.y }} onClick={event => { event.stopPropagation(); setDraft(null); setSelected(comment.id); }} aria-label={`${index + 1}번 코멘트`}>{index + 1}</button>; })}
      {draft && (() => { const position = point({ ...draft, normalized: true }); return <section className="flora-comment-card draft" style={{ left: Math.min(position.x + 18, window.scrollX + window.innerWidth - 342), top: position.y + 18 }} onClick={event => event.stopPropagation()}>
        <textarea autoFocus value={body} onChange={event => setBody(event.target.value)} placeholder="코멘트를 입력하세요" onKeyDown={event => { if ((event.metaKey || event.ctrlKey) && event.key === "Enter") save(); }} />
        <div><button onClick={() => setDraft(null)}>취소</button><button className="primary" onClick={save} disabled={!body.trim()}>등록</button></div>
      </section>; })()}
      {selectedComment && (() => { const position = point(selectedComment); return <section className="flora-comment-card saved" style={{ left: Math.min(position.x + 18, window.scrollX + window.innerWidth - 342), top: position.y + 18 }} onClick={event => event.stopPropagation()}>
        <header><strong>{selectedComment.source === "publisher" ? `배포자 · ${selectedComment.author}` : selectedComment.author}</strong><time>{new Date(selectedComment.createdAt).toLocaleString("ko-KR")}</time></header>
        <p>{selectedComment.body}</p>
        <div>{(!selectedComment.immutable || googleUser?.email === selectedComment.authorEmail) && <button className="danger" onClick={() => remove(selectedComment.id)}>삭제</button>}{selectedComment.immutable && googleUser?.email === selectedComment.authorEmail && <button onClick={() => edit(selectedComment)}>수정</button>}<button onClick={() => setSelected(null)}>닫기</button></div>
      </section>; })()}
    </div>}
    {enabled && <aside className="flora-comment-auth">{googleUser ? <><img src={googleUser.picture} alt="" /><span><strong>{googleUser.name}</strong><small>{googleUser.email}</small></span><button onClick={() => { sessionStorage.removeItem("flora:comment-google"); sessionStorage.removeItem("flora:comment-user"); setGoogleCredential(""); setGoogleUser(null); }}>로그아웃</button></> : <><span><strong>익명으로 작성 중</strong><small>로그인하면 이름이 표시됩니다</small></span><div ref={googleButton} /></>}</aside>}
  </>;
}
